<template>
  <div class="collections-panel">
    <SidepanelHeader icon="collection" :count="collectionsCount" :title="headerTitle" />
    <div class="buttons-container" v-if="!conversationScope.breadcrumb.length">
      <BbBtn class="create-button" size="small" type="secondary" @click="createNewCollection">
        <BbIcon size="small" name="plus-small" />
        <TextBody i18n-key="collections.newCollection" />
      </BbBtn>
    </div>

    <CollectionsContainer
      v-show="conversationScope.breadcrumb.length === 0"
      :current-collections="currentCollections"
    />
    <CollectionDetailsContainer
      v-show="conversationScope.breadcrumb.length === 1"
      :collection-id="conversationScope.collection.collectionId"
      :collections-count="collectionsCount"
      @navigate="goBack"
    />
    <DocumentContainer v-if="conversationScope.breadcrumb.length === 2" @navigate="goBack" />
  </div>
</template>

<script>
import { onUnmounted, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n/dist/vue-i18n';

import useCollections from '~/composables/useCollections';
import useConversations from '~/composables/useConversations';
import useModals from '~/composables/useModals';

import CollectionsContainer from '~/components/conversation/collections/CollectionsContainer';
import CollectionDetailsContainer from '~/components/conversation/collections/CollectionDetailsContainer';
import DocumentContainer from '~/components/conversation/DocumentContainer';
import TextBody from '~/components/common/TextBody.vue';
import SidepanelHeader from '~/components/conversation/sidepanelGeneralComponents/SidepanelHeader.vue';

export default {
  components: { SidepanelHeader, TextBody, DocumentContainer, CollectionsContainer, CollectionDetailsContainer },
  setup() {
    const { t } = useI18n();
    const { currentCollections } = useCollections();
    const { setConversationScope, conversationScope } = useConversations();
    const { setCreateCollectionPopup } = useModals();
    const confirmDeletePopupOpened = ref(false);

    const collectionsCount = computed(() => currentCollections.value.length);
    const headerTitle = computed(() => {
      let title = t('collections.collections');
      if (conversationScope.value.breadcrumb.length === 2) {
        title += '...';
      }
      conversationScope.value.breadcrumb.forEach((crumb) => {
        title += ' /' + crumb.title;
      });
      return title;
    });

    const createNewCollection = () => setCreateCollectionPopup({ open: true, collection: {} });

    const goBack = () => {
      if (conversationScope.value.breadcrumb.length > 1) {
        const breadcrumb = conversationScope.value.breadcrumb.slice(0, -1);
        setConversationScope({ breadcrumb });
      } else {
        const breadcrumb = [];
        setConversationScope({ isScoped: false, collection: {}, breadcrumb });
      }
    };

    onUnmounted(() => setConversationScope({ isScoped: false, collection: {}, breadcrumb: [] }));

    return {
      conversationScope,
      confirmDeletePopupOpened,
      currentCollections,
      goBack,
      t,
      collectionsCount,
      createNewCollection,
      headerTitle,
    };
  },
};
</script>

<style scoped lang="scss">
* {
  box-sizing: border-box;
}

.collections-panel {
  @include media-breakpoint-up('sm') {
    display: flex;
  }

  max-height: calc(100vh - 80px);

  // take into consideration the margins
  height: 100%;
  display: none;
  flex-direction: column;
  background-color: $bb-neutral-0;
  border-radius: $bb-spacing-8;
  border: 1px solid #cecdcb;
  box-shadow: 0 0 $bb-spacing-2 0 #0000001a;
  align-items: center;
  min-width: 476px;
  max-width: 476px;
  overflow-x: hidden;
  overflow-y: auto;

  .buttons-container {
    padding: $bb-spacing-2 $bb-spacing-4;
    background-color: $bb-neutral-0;
    width: 100%;

    .create-button {
      width: 100%;

      :deep(.v-btn__content) {
        gap: $bb-spacing-1;
      }

      &:hover {
        background-color: inherit;
      }
    }
  }
}
</style>
